import '../../motion.css';
import './inspect.css';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
// Radix's own surface registration (its Dialog overlay uses it); `radix-ui` exports it from
// `/internal`, and package-lock pins the version.
import { DismissableLayer } from 'radix-ui/internal';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { inspectCopy } from '@/i18n/ns/inspect';
import { GeoClock, bounds, labelPlacement, labelPosition } from './geometry';
import { HoverGate, classifyPointer, escapeStep, isTypingTarget, selectionMode } from './gestures';
import { InspectHostContext, type HoverState, type InspectHost } from './context';
import { useAddMode } from './ui-store';
import { NestPicker } from './picker';
import { PropertySheet } from './property-sheet';
import { InspectSheet } from './phone-sheet';
import { TargetList } from './target-list';
import { AnchoredToTarget } from './anchor';
import type { InspectAdapter, InspectSession, Rect } from './types';

const LABEL_HEIGHT = 22;

function Outline({
  rects,
  tone,
  order,
  label,
  viewport,
  saved = false,
}: {
  rects: Rect[];
  tone: 'hover' | 'selected' | 'stale';
  order?: number;
  /** Just saved: one halo while the target re-renders with its new value. */
  saved?: boolean;
  label?: string | null;
  viewport: Rect;
}) {
  // A repeated target (a column, every row using a style) is one target: one outline on its
  // first instance (the header) and a quiet tint on the rest, never a box per cell.
  const [primary, ...rest] = rects;
  const box = primary ?? bounds(rects);
  if (!box) return null;
  const placement = labelPlacement(box, viewport, LABEL_HEIGHT);
  const { x: labelX, y: labelY } = labelPosition(box, placement, LABEL_HEIGHT);
  return (
    <>
      <span
        key={saved ? 'saved' : 'outline'}
        className={saved ? 'pc-inspect-outline pc-m-halo' : 'pc-inspect-outline'}
        data-tone={tone}
        style={{
          transform: `translate(${box.x}px, ${box.y}px)`,
          width: box.width,
          height: box.height,
        }}
        aria-hidden="true"
      />
      {rest.map((r, i) => (
        <span
          key={i}
          className="pc-inspect-tint"
          data-tone={tone}
          style={{ transform: `translate(${r.x}px, ${r.y}px)`, width: r.width, height: r.height }}
          aria-hidden="true"
        />
      ))}
      {(label || order) && (
        <span
          className="pc-inspect-label"
          data-tone={tone}
          data-placement={placement}
          style={{ transform: `translate(${labelX}px, ${labelY}px)` }}
          aria-hidden="true"
        >
          {order ? <span className="pc-inspect-order">{order}</span> : null}
          {label}
        </span>
      )}
    </>
  );
}

/**
 * The host layer over the canvas while Inspect is on (INSPECTOR-SPEC §2, §6). It catches
 * every pointer gesture so nothing in the artifact or app underneath activates, asks the
 * adapter what is under the pointer (at most 30 times a second, one at a time, stale answers
 * dropped), shows the canonical label INS resolves, and turns a click or tap into an INS
 * `add`. Selected targets keep numbered outlines that follow scroll and resize, and the
 * property sheet anchors to them (a bottom sheet on a phone).
 *
 * Mount it in `canvasOverlaySlot`: a non-scrolling, `position: relative` wrapper that exactly
 * covers the canvas's scrolling viewport, as the scroller's sibling. Never inside the scroller
 * itself, or the layer scrolls away with the content and stops covering what is visible.
 */
export function InspectLayer({
  adapter,
  session,
  phone = false,
  onMention,
  onHandoff,
  clock: externalClock,
}: {
  adapter: InspectAdapter;
  session: InspectSession;
  phone?: boolean;
  /** Phone: focus the composer with the selection attached. */
  onMention?: () => void;
  /** An unsupported direct edit: hand the selection to the chat. */
  onHandoff?: () => void;
  /**
   * A geometry clock shared with the surface's bridge (a frame: useFrameInspectAdapter), so
   * the bridge's rect and scroll requests carry the layer's current identity.
   */
  clock?: GeoClock;
}): ReactNode {
  const copy = useNamespaceCopy(inspectCopy);
  const root = useRef<HTMLDivElement>(null);
  // The layer keeps its clicks from the artifact and the app (onClick below), which Radix reads
  // as an intercepted press; as a dismissable surface, a press here still closes an open menu or
  // popover, such as the colour swatches (DESIGN-SPEC §6.9).
  const registerSurface = DismissableLayer.useDismissableLayerSurface();
  const layerRef = useCallback(
    (node: HTMLDivElement | null) => {
      root.current = node;
      registerSurface(node);
    },
    [registerSurface],
  );
  const clock = useRef<GeoClock | null>(null);
  clock.current =
    externalClock ?? clock.current ?? new GeoClock(session.sessionId, session.revision);
  clock.current.rebind(session.sessionId, session.revision);
  const gate = useRef(new HoverGate());
  const [hover, setHover] = useState<HoverState>(null);
  const [addMode, setAddMode] = useAddMode(session.sessionId);
  const [editing, setEditing] = useState(false);
  // The target an edit was just saved to: its outline gets one halo (the value morphs in place).
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [, redraw] = useReducer((n: number) => n + 1, 0);
  const [viewport, setViewport] = useState<Rect>({ x: 0, y: 0, width: 0, height: 0 });
  const labels = useRef(new Map<string, string>());
  // The latest session, for effects and callbacks that must not resubscribe on every render.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  // Every hit test is announced to INS first; INS refuses answers it didn't see asked for.
  // INS refuses (inactive, rate-limited, stale geometry) by throwing: then there is no request.
  const request = useCallback((kind: 'hover' | 'click') => {
    const id = clock.current!.next(kind);
    try {
      sessionRef.current.beginRequest(id, kind);
      return id;
    } catch {
      return null;
    }
  }, []);
  // The one hover hit test in flight, and a token that retires any late answer.
  const hoverRequest = useRef<string | null>(null);
  const hoverToken = useRef(0);
  const down = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  // Pointers on the layer now: a second finger makes it a pinch, never a selection.
  const pointers = useRef(new Set<number>());
  // The last touch point while a finger drags: the drag scrolls the canvas underneath.
  const dragFrom = useRef<{ x: number; y: number } | null>(null);
  const hoverTimer = useRef<number | null>(null);
  const active = session.state === 'on';
  const on = active || session.state === 'frozen';

  // Geometry: a new document or a scroll/resize stales what is in flight; new boxes redraw.
  // The layer measures itself whenever it is (re)shown and whenever it resizes.
  useEffect(() => {
    if (!on) return;
    const measure = () => {
      const r = root.current?.getBoundingClientRect();
      if (r) setViewport({ x: 0, y: 0, width: r.width, height: r.height });
    };
    measure();
    const observer =
      typeof ResizeObserver === 'function' && root.current ? new ResizeObserver(measure) : null;
    if (observer && root.current) observer.observe(root.current);
    const off = adapter.subscribe?.((what) => {
      if (what === 'document') {
        clock.current!.bumpDocument();
        sessionRef.current.invalidate?.('document_replaced');
        labels.current.clear();
        setHover(null);
      } else if (what === 'geometry') clock.current!.bumpEpoch();
      if (what !== 'rects') {
        measure();
        // The clock has advanced; only now are watched boxes asked for again, so the answer
        // carries the current identity (one owner: the layer).
        adapter.refresh?.();
      }
      redraw();
    });
    return () => {
      observer?.disconnect();
      off?.();
    };
  }, [adapter, on]);

  // A new session or revision starts with no labels or pointers held over from the old one.
  useEffect(() => {
    labels.current.clear();
    setHover(null);
    pointers.current.clear();
    down.current = null;
    dragFrom.current = null;
  }, [session.sessionId, session.revision]);

  // Keep surface geometry only for what is selected now.
  const selectedNodeKey = (on ? session.targets.filter((t) => t.status === 'current') : [])
    .map((t) => t.nodeId)
    .join('|');
  useEffect(() => {
    adapter.retain?.(selectedNodeKey ? selectedNodeKey.split('|') : []);
    return () => adapter.retain?.([]);
  }, [adapter, selectedNodeKey]);
  useEffect(
    () => () => {
      if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    },
    [],
  );

  /**
   * Forget the pointer: cancel the hover hit test actually in flight, stop a scheduled flush and
   * retire any late answer. It never announces a new request (INS refuses them when inactive).
   */
  const dropHover = useCallback(() => {
    hoverToken.current += 1;
    if (hoverRequest.current) adapter.cancel(hoverRequest.current);
    hoverRequest.current = null;
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    gate.current.reset();
    setHover(null);
  }, [adapter]);

  // Leaving Inspect drops hover and pending requests, never the chips.
  useEffect(() => {
    if (active) return;
    dropHover();
    setAddMode(false);
    pointers.current.clear();
    down.current = null;
    dragFrom.current = null;
  }, [active, dropHover, setAddMode]);

  const local = (event: { clientX: number; clientY: number }) => {
    const r = root.current!.getBoundingClientRect();
    return { x: event.clientX - r.left, y: event.clientY - r.top };
  };

  // A waiting hover point is always sent once the interval allows: after a throttled move, or
  // after an answer that arrived too soon after the last send.
  const hoverAtRef = useRef<(point: { x: number; y: number }) => void>(() => {});
  const scheduleFlush = useCallback(() => {
    if (hoverTimer.current !== null || !gate.current.waitingPoint) return;
    const wait = gate.current.msUntilReady(performance.now());
    if (!Number.isFinite(wait)) return; // one is outstanding: its answer flushes
    hoverTimer.current = window.setTimeout(() => {
      hoverTimer.current = null;
      const next = gate.current.flush(performance.now());
      if (next) hoverAtRef.current(next);
      else scheduleFlush();
    }, wait);
  }, []);

  const hoverAt = useCallback(
    async (point: { x: number; y: number }) => {
      const token = hoverToken.current;
      const id = request('hover');
      if (!id) {
        gate.current.settle(performance.now());
        return;
      }
      hoverRequest.current = id.requestId;
      const candidate = await adapter.hitTest(point, id).catch(() => null);
      if (hoverRequest.current === id.requestId) hoverRequest.current = null;
      // Dropped while in flight (left the layer, Inspect off): this answer is no one's.
      if (token !== hoverToken.current) return;
      const settledNext = gate.current.settle(performance.now());
      if (!settledNext) scheduleFlush();
      if (!clock.current!.isCurrent(id, 'hover')) {
        if (settledNext) void hoverAt(settledNext);
        return;
      }
      if (!candidate) setHover(null);
      else {
        const known = labels.current.get(candidate.pid) ?? null;
        setHover({ candidate, label: known });
        if (!known)
          void session
            .resolve(candidate, id)
            .catch(() => null)
            .then((resolved) => {
              if (!resolved || !clock.current!.isCurrent(id)) return;
              labels.current.set(candidate.pid, resolved.label);
              setHover((h) =>
                h && h.candidate.pid === candidate.pid ? { ...h, label: resolved.label } : h,
              );
            });
      }
      if (settledNext) void hoverAt(settledNext);
    },
    [adapter, session, scheduleFlush],
  );
  hoverAtRef.current = (point) => void hoverAt(point);

  const choose = useCallback(
    (targetId: string, mode: 'add' | 'replace') => {
      const already = session.targets.some((t) => t.targetId === targetId);
      try {
        if (mode === 'add' && already) session.remove(targetId);
        // Past the limit nothing is dropped: the tray explains, and the person chooses.
        else if (mode === 'add' && session.targets.length >= session.limit) return;
        else session.add(targetId, { replace: mode === 'replace' });
      } catch {
        // INS refused (the click went stale, the session changed): nothing is selected.
      }
    },
    [session],
  );

  const select = useCallback(
    async (point: { x: number; y: number }, mods: { metaKey?: boolean; ctrlKey?: boolean }) => {
      const id = request('click');
      if (!id) return;
      const candidate = await adapter.hitTest(point, id).catch(() => null);
      if (!candidate || !clock.current!.isCurrent(id, 'click')) return;
      const resolved = await session.resolve(candidate, id).catch(() => null);
      if (!resolved || !clock.current!.isCurrent(id, 'click')) return;
      labels.current.set(candidate.pid, resolved.label);
      choose(resolved.targetId, selectionMode(mods, addMode));
    },
    [adapter, session, addMode, choose],
  );

  const escape = useCallback(() => {
    const step = escapeStep({ editing, selected: session.targets.length, on: active });
    if (step === 'cancel-edit') {
      session.cancel();
      setEditing(false);
    } else if (step === 'clear') session.clear();
    else if (step === 'leave') session.setOn(false);
  }, [editing, session, active]);

  useEffect(() => {
    if (!on) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const frame = root.current?.closest('[data-testid="artifact-frame"]');
      const scope = frame?.closest('.ap-saved-chrome') ?? frame ?? root.current;
      const canvas = scope?.closest('.ws-canvas[data-canvas-sheet="true"]');
      const modal = target?.closest(
        '[role="dialog"], [aria-modal="true"], [data-radix-popper-content-wrapper]',
      );
      if (
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        !target ||
        !scope?.contains(target) ||
        isTypingTarget(target) ||
        (modal && modal !== canvas)
      )
        return;
      // Consume this frame's inspector Escape before the document canvas-close listener.
      // Its owning CanvasSheet handles Escape on bubble; nested modals and typing
      // remain with their existing owners.
      event.preventDefault();
      event.stopPropagation();
      escape();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [on, escape]);

  const host: InspectHost = useMemo(
    () => ({
      adapter,
      session,
      clock: clock.current!,
      request,
      phone,
      hover,
      setHover,
      addMode,
      setAddMode,
      editing,
      setEditing,
      // INS names targets; the surface knows nodes. Geometry always goes by the node id.
      rectsFor: (targetId) => {
        const nodeId = session.targets.find((t) => t.targetId === targetId)?.nodeId;
        return nodeId ? adapter.rects(nodeId, clock.current!.current()) : [];
      },
      viewport,
      escape,
      choose,
      markSaved: (targetIds: string[]) => setSavedAt(targetIds.at(-1) ?? null),
    }),
    // redraw is part of the geometry; recompute rects on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adapter, session, phone, hover, addMode, editing, viewport, escape, choose, request],
  );

  if (session.state === 'off' || session.state === 'unavailable') return null;
  const targets = session.targets;
  const last = targets.at(-1) ?? null;
  const selectedNodes = new Set(targets.map((t) => t.nodeId));
  const hoverRects = hover && !selectedNodes.has(hover.candidate.pid) ? hover.candidate.rects : [];

  return (
    <InspectHostContext.Provider value={host}>
      <div
        ref={layerRef}
        className="pc-inspect-layer"
        data-inspect-layer=""
        data-state={session.state}
        data-testid="inspect-layer"
        // Catch every gesture while choosing, so nothing underneath activates.
        data-catching={active ? 'true' : undefined}
        onPointerMove={(event) => {
          if (!active || event.target !== event.currentTarget) return;
          if (event.pointerType === 'touch') {
            // A finger dragging scrolls what is underneath (the layer catches it, so forward it).
            const at = local(event);
            if (
              dragFrom.current &&
              pointers.current.size === 1 &&
              down.current?.pointerId === event.pointerId
            ) {
              adapter.scrollIntent?.(dragFrom.current.x - at.x, dragFrom.current.y - at.y);
              dragFrom.current = at;
            }
            return;
          }
          const now = performance.now();
          const point = gate.current.offer(local(event), now);
          if (point) return void hoverAt(point);
          // Throttled: the last move is hit-tested once the interval passes.
          scheduleFlush();
        }}
        onPointerLeave={dropHover}
        onPointerDown={(event) => {
          if (!active || event.target !== event.currentTarget) return;
          pointers.current.add(event.pointerId);
          // Own every pointer pressed on the layer until it is released, even outside it, so its
          // release (or lost capture) is always seen, a second finger included.
          try {
            event.currentTarget.setPointerCapture?.(event.pointerId);
          } catch {
            // An already-released pointer: nothing to capture.
          }
          if (pointers.current.size > 1) {
            // A second pointer: a pinch or a palm. Nothing is selected and nothing scrolls.
            down.current = null;
            dragFrom.current = null;
            return;
          }
          down.current = { ...local(event), pointerId: event.pointerId };
          dragFrom.current = event.pointerType === 'touch' ? local(event) : null;
        }}
        onPointerUp={(event) => {
          pointers.current.delete(event.pointerId);
          if (!active || !down.current || down.current.pointerId !== event.pointerId) return;
          const up = local(event);
          const kind = classifyPointer(down.current, up);
          down.current = null;
          dragFrom.current = null;
          if (kind === 'drag') return;
          event.preventDefault();
          void select(up, event);
        }}
        onLostPointerCapture={(event) => {
          // Released somewhere we don't see (outside the window, a system gesture): forget it.
          pointers.current.delete(event.pointerId);
          if (down.current?.pointerId === event.pointerId) {
            down.current = null;
            dragFrom.current = null;
          }
        }}
        onPointerCancel={(event) => {
          // The browser took the gesture (a system scroll, an interruption): no selection.
          pointers.current.delete(event.pointerId);
          down.current = null;
          dragFrom.current = null;
        }}
        onClick={(event) => {
          // The gesture is the host's; never the artifact's or the app's. A panel's own
          // buttons keep their clicks.
          if (event.target !== event.currentTarget) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onWheel={(event) => {
          // The layer sits over the scroller; scrolling it scrolls what is underneath. A wheel in
          // the layer's own panels (a field, the sheet) stays theirs.
          if (active && event.target === event.currentTarget)
            adapter.scrollIntent?.(event.deltaX, event.deltaY);
        }}
      >
        {/* On a desktop the toolbar carries the hint; a phone has no toolbar room. */}
        {phone && active && !targets.length && !hover && (
          <p className="pc-inspect-hint pc-m-fade" role="status">
            {copy.hintTouch}
          </p>
        )}
        {active && hoverRects.length > 0 && (
          <Outline
            rects={hoverRects}
            tone="hover"
            // The same localized manifest label as selection; no generic label while resolving.
            label={hover?.label}
            viewport={viewport}
          />
        )}
        {targets.map((t) => (
          <Outline
            key={t.targetId}
            rects={host.rectsFor(t.targetId)}
            tone={t.status === 'current' ? 'selected' : 'stale'}
            order={targets.length > 1 ? t.order : undefined}
            saved={savedAt === t.targetId}
            label={
              t.status === 'current'
                ? [t.label, t.context].filter(Boolean).join(' · ')
                : session.subjectLabel
                  ? formatCopy(copy.staleSubject, { subject: session.subjectLabel })
                  : copy.staleGeneric
            }
            viewport={viewport}
          />
        ))}
        <span className="sr-only" role="status" aria-live="polite">
          {targets.length ? formatCopy(copy.selectedCount, { n: String(targets.length) }) : ''}
        </span>
        <TargetList session={session} />
        {last && !phone && active && (
          <AnchoredToTarget targetId={last.targetId}>
            <div className="pc-inspect-panel">
              <NestPicker targetId={last.targetId} nodeId={last.nodeId} />
              <PropertySheet
                session={session}
                targetIds={targets.map((t) => t.targetId)}
                onHandoff={onHandoff}
                onMention={onMention}
              />
            </div>
          </AnchoredToTarget>
        )}
        {last && phone && active && <InspectSheet onMention={onMention} onHandoff={onHandoff} />}
      </div>
    </InspectHostContext.Provider>
  );
}
